import { useEffect, useMemo, useState } from "react";
import type { GraphyClient } from "../api/client";
import { ApiError } from "../api/client";
import type {
  CommunitiesResult,
  Direction,
  ExplorerMode,
  HubsResult,
  NeighborhoodResult,
  NodeRef,
  PathResult,
  Provenance,
} from "../api/types";
import type { ViewState } from "../state/viewState";
import { GraphCanvas } from "../graph/GraphCanvas";
import { Legend } from "../components/Legend";
import { ErrorState, Loading } from "../components/states";
import { Icon } from "../components/icons";
import { T } from "../i18n";

// GY-034 explorer, controlled by the shared URL ViewState (GY-035): mode,
// filters, origin/destination, and view are all persisted in the link so the
// exact explorer view can be restored or shared. Fetched data is local.

interface Props {
  client: GraphyClient;
  projectId: string;
  snapshotId: string;
  nodes: NodeRef[];
  state: ViewState;
  patch: (p: Partial<ViewState>) => void;
  onViewSource: (id: string) => void;
}

export function Explorer({ client, projectId, snapshotId, nodes, state, patch, onViewSource }: Props) {
  const scope = { projectId, snapshotId };
  const { mode, community, search, provenance, depth, direction, target, undirected, view, seed } = state;

  const [neighbors, setNeighbors] = useState<NeighborhoodResult | null>(null);
  const [path, setPath] = useState<PathResult | null>(null);
  const [comms, setComms] = useState<CommunitiesResult | null>(null);
  const [hubs, setHubs] = useState<HubsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const label = (id: string) => nodeById.get(id)?.label ?? id;
  const communityOf = (id: string) => nodeById.get(id)?.community ?? 0;
  const prov = provenance === "all" ? undefined : provenance;

  useEffect(() => {
    if (!snapshotId) return;
    let live = true;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        if (mode === "neighborhood" && seed) {
          setNeighbors(await client.neighborhood({ ...scope, origin: seed, direction, depth, provenance: prov }));
        } else if (mode === "path" && seed && target) {
          setPath(await client.shortestPath({ ...scope, origin: seed, target, undirected, provenance: prov }));
        } else if (mode === "communities") {
          setComms(await client.communities(scope));
        } else if (mode === "hubs") {
          setHubs(await client.hubs(scope, 10));
        }
      } catch (e) {
        if (e instanceof ApiError || e instanceof Error) setError(true);
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, seed, target, depth, direction, undirected, provenance, snapshotId]);

  const scopeIds: string[] = useMemo(() => {
    if (mode === "path") return path?.path ?? [];
    if (mode === "neighborhood") return neighbors?.nodes ?? [];
    return nodes.map((n) => n.id);
  }, [mode, path, neighbors, nodes]);

  const visible = nodes.filter(
    (v) =>
      scopeIds.includes(v.id) &&
      (community === "all" || String(v.community) === community) &&
      (!search || (v.label + " " + (v.file ?? "")).toLowerCase().includes(search.toLowerCase())),
  );
  const visibleIds = new Set(visible.map((v) => v.id));
  const edges: [string, string][] =
    mode === "neighborhood"
      ? (neighbors?.edges ?? []).filter(([s, t]) => visibleIds.has(s) && visibleIds.has(t))
      : mode === "path" && path?.path
        ? path.path.slice(0, -1).map((id, i) => [id, path.path![i + 1]] as [string, string])
        : [];
  const truncated = mode === "neighborhood" && neighbors?.truncated;

  return (
    <div className="explorer">
      <div className="page-head">
        <h2>{T.app.explore}</h2>
        <p>Trace neighbourhoods, paths, communities and hubs across the graph.</p>
      </div>
      <div className="explorer-modes" role="tablist" aria-label={T.app.explore}>
        {(Object.keys(T.explorer.modes) as ExplorerMode[]).map((id) => (
          <button
            key={id}
            role="tab"
            aria-selected={mode === id}
            className={mode === id ? "selected-tab" : ""}
            onClick={() => patch({ mode: id, community: "all", search: "" })}
          >
            {T.explorer.modes[id]}
          </button>
        ))}
      </div>

      <div className="explorer-controls">
        <label>
          {T.explorer.origin}
          <select aria-label={T.explorer.origin} value={seed} onChange={(e) => patch({ seed: e.target.value })}>
            {nodes.map((n) => (
              <option key={n.id} value={n.id}>
                {n.label}
              </option>
            ))}
          </select>
        </label>
        {mode === "path" ? (
          <label>
            {T.explorer.destination}
            <select aria-label={T.explorer.destination} value={target} onChange={(e) => patch({ target: e.target.value })}>
              {nodes.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.label}
                </option>
              ))}
            </select>
          </label>
        ) : (
          mode !== "hubs" && (
            <label>
              {T.explorer.community}
              <select aria-label={T.explorer.community} value={community} onChange={(e) => patch({ community: e.target.value })}>
                <option value="all">{T.map.all}</option>
                {(comms?.communities ?? []).map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {`#${c.id} (${c.members.length})`}
                  </option>
                ))}
              </select>
            </label>
          )
        )}
        <label>
          {T.explorer.provenance}
          <select aria-label={T.explorer.provenance} value={provenance} onChange={(e) => patch({ provenance: e.target.value as Provenance | "all" })}>
            <option value="all">{T.map.all}</option>
            {(["extracted", "inferred", "ambiguous"] as Provenance[]).map((p) => (
              <option key={p} value={p}>
                {T.provenanceLabel[p]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="explorer-filter">
        <label className="search">
          <div className="field-search">
            <Icon.search size={15} />
            <input aria-label={T.explorer.search} value={search} onChange={(e) => patch({ search: e.target.value })} placeholder={T.explorer.search} />
          </div>
        </label>
        {mode === "neighborhood" && (
          <>
            <label>
              {T.explorer.depth}
              <select aria-label={T.explorer.depth} value={depth} onChange={(e) => patch({ depth: +e.target.value })}>
                {[1, 2, 3, 4].map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            <select aria-label={T.explorer.direction} value={direction} onChange={(e) => patch({ direction: e.target.value as Direction })}>
              <option value="both">{T.explorer.directionBoth}</option>
              <option value="incoming">{T.explorer.directionIncoming}</option>
              <option value="outgoing">{T.explorer.directionOutgoing}</option>
            </select>
          </>
        )}
        {mode === "path" && (
          <label className="checkbox-label">
            <input type="checkbox" checked={undirected} onChange={(e) => patch({ undirected: e.target.checked })} />
            {T.explorer.ignoreDirection}
          </label>
        )}
        {mode !== "hubs" && (
          <button className="btn" onClick={() => patch({ view: view === "graph" ? "list" : "graph" })}>
            {view === "graph" ? T.common.listView : T.common.graphView}
          </button>
        )}
        {seed && (
          <button className="btn" onClick={() => onViewSource(seed)}>
            {T.source.viewSource}
          </button>
        )}
      </div>

      {truncated && <div className="banner banner-partial" role="status">{T.explorer.truncated}</div>}
      {mode === "neighborhood" && neighbors?.missingOrigin && (
        <div className="banner banner-error" role="alert">{T.explorer.missingOrigin}</div>
      )}
      {mode === "path" && path && (
        <div className="path-status" role="status">
          {path.path ? `${T.explorer.pathHops(path.path.length - 1)} · ${path.path.map(label).join(" → ")}` : T.explorer.noPath}
        </div>
      )}

      {error ? (
        <ErrorState />
      ) : loading ? (
        <Loading />
      ) : mode === "hubs" ? (
        <HubsTable hubs={hubs} label={label} onPick={(id) => patch({ seed: id, mode: "neighborhood" })} />
      ) : (
        <>
          <div className="explorer-count" aria-live="polite">
            {T.explorer.count(visible.length, nodes.length)}
          </div>
          {view === "graph" ? (
            <GraphCanvas
              nodes={visible}
              edges={edges}
              communityOf={communityOf}
              labelOf={label}
              selected={seed}
              onSelect={(id) => patch({ seed: id })}
              edgeProvenance={() => prov}
              ariaLabel={T.app.explore}
            />
          ) : (
            <ul className="node-list">
              {visible.map((n) => (
                <li key={n.id}>
                  <button className="link" onClick={() => patch({ seed: n.id })}>
                    {n.label}
                  </button>
                  <span className="muted">{n.file}</span>
                </li>
              ))}
              {visible.length === 0 && <li className="muted">{T.common.noMatch}</li>}
            </ul>
          )}
          <Legend />
        </>
      )}
    </div>
  );
}

function HubsTable({ hubs, label, onPick }: { hubs: HubsResult | null; label: (id: string) => string; onPick: (id: string) => void }) {
  if (!hubs) return null;
  return (
    <div className="hubs-table">
      <h3>{T.explorer.hubsTitle}</h3>
      <p className="muted">{hubs.disclaimer}</p>
      <ol>
        {hubs.hubs.map((h) => (
          <li key={h.node}>
            <button className="link" onClick={() => onPick(h.node)}>
              {label(h.node)}
            </button>
            <span className="muted">
              {h.degree} ({h.inDegree} in / {h.outDegree} out)
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
